// The immersive Git client's prominent LEFT control (design 2.1): a full-height
// workspace dropdown that replaces the old repo `<select>`. The trigger shows
// the active repo basename + its current branch; opening it lists every open
// workspace. A workspace that already is a repo is a selectable row (with a
// check when it's the active one); a workspace with no repo offers an inline
// "Initialize Git repository" action. The panel is a portalled `role=menu`
// with arrow / Enter / Escape keyboard support; an empty workspace set renders
// a disabled "No workspace open" trigger.

import { useCallback, useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { GitBranch, FolderGit2, Plus, Check, ChevronDown } from 'lucide-react';
import { basename } from '../../fs/containment';

/** One open workspace and the repo it resolves to (null when it isn't a repo). */
export interface GitWorkspaceEntry {
  path: string;
  repoRoot: string | null;
}

export interface GitWorkspaceSwitcherProps {
  workspaces: GitWorkspaceEntry[];
  activeRoot: string | null;
  /** Current branch of the active repo, shown beside the name when known. */
  branchLabel?: string;
  onSelectRepo(repoRoot: string): void;
  onInit(path: string): void | Promise<void>;
}

export function GitWorkspaceSwitcher({
  workspaces,
  activeRoot,
  branchLabel,
  onSelectRepo,
  onInit,
}: GitWorkspaceSwitcherProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const hasWorkspaces = workspaces.length > 0;

  // Label the trigger by the active repo (preferring its resolved root, so a
  // repo nested below the workspace folder reads by its real name).
  const activeEntry = activeRoot
    ? workspaces.find((w) => w.repoRoot === activeRoot)
    : undefined;
  const activeLabel = activeRoot
    ? basename(activeEntry?.repoRoot ?? activeRoot) || activeRoot
    : hasWorkspaces
      ? 'Select repository'
      : 'No workspace open';

  // Position the full-height panel under the trigger and close on outside click.
  useEffect(() => {
    if (!open) return;
    const place = (): void => {
      const b = btnRef.current?.getBoundingClientRect();
      if (b) setPos({ top: b.bottom + 4, left: b.left, width: Math.max(b.width, 260) });
    };
    place();
    const onDocClick = (e: MouseEvent): void => {
      if (
        !menuRef.current?.contains(e.target as Node) &&
        !btnRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDocClick);
    return () => {
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [open]);

  // Move keyboard focus to the first row when the panel opens (standard menu UX).
  useEffect(() => {
    if (!open) return;
    const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
    first?.focus();
  }, [open]);

  const close = useCallback((refocus = true): void => {
    setOpen(false);
    if (refocus) btnRef.current?.focus();
  }, []);

  const moveFocus = (dir: 1 | -1): void => {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (items.length === 0) return;
    const idx = items.indexOf(document.activeElement as HTMLElement);
    const next = idx < 0 ? (dir === 1 ? 0 : items.length - 1) : (idx + dir + items.length) % items.length;
    items[next]?.focus();
  };

  const selectRepo = (repoRoot: string): void => {
    close();
    onSelectRepo(repoRoot);
  };

  const initRepo = (path: string): void => {
    close();
    void onInit(path);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="mp-git-wsswitcher__trigger"
        aria-label="Switch repository"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={!hasWorkspaces}
        title={activeRoot ?? undefined}
        onClick={() => {
          if (hasWorkspaces) setOpen((o) => !o);
        }}
      >
        <GitBranch size={15} aria-hidden className="mp-git-wsswitcher__icon" />
        <span className="mp-git-wsswitcher__name">{activeLabel}</span>
        {branchLabel ? (
          <span className="mp-git-wsswitcher__branch" title={`On branch ${branchLabel}`}>
            {branchLabel}
          </span>
        ) : null}
        <ChevronDown size={14} aria-hidden className="mp-git-wsswitcher__chev" />
      </button>
      {open &&
        hasWorkspaces &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label="Workspaces"
            className="mp-git-wsswitcher__panel"
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, bottom: 8 }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                close();
              } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                moveFocus(1);
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                moveFocus(-1);
              }
            }}
          >
            <div className="mp-git-wsswitcher__heading" aria-hidden>
              Workspaces
            </div>
            <div className="mp-git-wsswitcher__list">
              {workspaces.map((w) => {
                const label = basename(w.path) || w.path;
                const repoRoot = w.repoRoot;
                if (repoRoot) {
                  const active = repoRoot === activeRoot;
                  return (
                    <button
                      key={w.path}
                      type="button"
                      role="menuitem"
                      className="mp-git-wsswitcher__row"
                      aria-current={active ? true : undefined}
                      onClick={() => selectRepo(repoRoot)}
                    >
                      <FolderGit2 size={15} aria-hidden className="mp-git-wsswitcher__row-icon" />
                      <span className="mp-git-wsswitcher__row-name">{label}</span>
                      {active ? (
                        <Check size={15} aria-hidden className="mp-git-wsswitcher__check" />
                      ) : null}
                    </button>
                  );
                }
                return (
                  <div key={w.path} className="mp-git-wsswitcher__norepo">
                    <div className="mp-git-wsswitcher__norepo-head">
                      <FolderGit2
                        size={15}
                        aria-hidden
                        className="mp-git-wsswitcher__row-icon mp-git-wsswitcher__row-icon--muted"
                      />
                      <span className="mp-git-wsswitcher__row-name">{label}</span>
                    </div>
                    <span className="mp-git-wsswitcher__hint">No git repository</span>
                    <button
                      type="button"
                      role="menuitem"
                      className="mp-git-wsswitcher__init"
                      onClick={() => initRepo(w.path)}
                    >
                      <Plus size={14} aria-hidden />
                      Initialize Git repository
                    </button>
                  </div>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
